import type { CSSProperties } from "react";
import { cx } from "../util";

export interface SpinnerProps {
  size?: number;
  /** Screen-reader label. Pass `null` when the spinner sits inside a labelled control. */
  label?: string | null;
  className?: string;
}

export function Spinner({ size = 18, label = "Loading", className }: SpinnerProps) {
  const style = { width: size, height: size } as CSSProperties;
  return (
    <span className={cx("gf-spinner", className)} style={style} role={label ? "status" : undefined} aria-hidden={label ? undefined : true}>
      <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
        <circle cx="12" cy="12" r="9.5" stroke="currentColor" strokeOpacity="0.18" strokeWidth="2.5" />
        <path d="M21.5 12a9.5 9.5 0 0 0-9.5-9.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
      {label ? <span className="gf-sr-only">{label}</span> : null}
    </span>
  );
}

export interface SkeletonProps {
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  /** Render N stacked text lines (the last one shorter). */
  lines?: number;
  circle?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function Skeleton({ width, height, radius, lines, circle, className, style }: SkeletonProps) {
  if (lines && lines > 1) {
    return (
      <span className={cx("gf-skeleton-lines", className)} aria-hidden="true" style={style}>
        {Array.from({ length: lines }, (_, i) => (
          <span key={i} className="gf-skeleton" style={{ width: i === lines - 1 ? "62%" : "100%", height: height ?? 12 }} />
        ))}
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className={cx("gf-skeleton", circle && "gf-skeleton--circle", className)}
      style={{
        width: width ?? (circle ? height : "100%"),
        height: height ?? 14,
        borderRadius: circle ? "50%" : radius,
        ...style,
      }}
    />
  );
}
